import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { buttonStyles } from '../css/commonStyles';
import { TickCheckmark } from './icons';
import {
  alertErrorSx,
  cancelBtnSx,
  colors,
  confirmBtnSx,
  dialogBtnGridSx,
  dialogTitleSx,
  fieldBlockStyles,
  fieldErrorTextSx,
  fieldLabelStyles,
  formFieldStyles,
  formGridSx,
  makeSelectSx,
  modalPaperStyles,
  requiredSpanSx,
  selectMenuProps,
} from '../css/revenueItems.styles';
import { createPaymentRequest } from 'helpers/paymentRequest.api';

const SERVICE_TYPE_OPTIONS = [
  { value: 'service_revenue', label: 'Service' },
  { value: 'product_revenue', label: 'Product' },
];

const CreatePaymentDialog = ({ open, onClose, currentDao, onSuccess }) => {
  const [serviceType, setServiceType] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [expiration, setExpiration] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const resetForm = () => {
    setServiceType('');
    setDescription('');
    setAmount('');
    setInvoiceNumber('');
    setExpiration('');
    setFormErrors({});
    setSubmitError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleConfirm = async () => {
    const newErrors = {};
    if (!serviceType) newErrors.serviceType = 'This field is required';
    if (!amount) newErrors.amount = 'This field is required';
    if (!expiration) newErrors.expiration = 'This field is required';
    setFormErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const days = parseInt(expiration, 10);
    const expiresDate = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
    expiresDate.setHours(23, 59, 59, 0);

    const body = {
      daoId: currentDao?._id,
      chainId: currentDao?.chainId,
      vaultAddress: currentDao?.vaultAddress,
      revenueType: serviceType,
      amount,
      currency: 'USDT',
      description: description.trim() || undefined,
      externalRefId: invoiceNumber.trim() || undefined,
      expiresAt: expiresDate.toISOString(),
    };

    try {
      setIsSubmitting(true);
      setSubmitError('');
      const res = await createPaymentRequest(body);
      resetForm();
      onClose();
      onSuccess(res?.data?.paymentUrl || '');
    } catch (err) {
      setSubmitError(err?.response?.data?.error?.message || err?.message || 'Failed to create payment request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: modalPaperStyles }}>
      <DialogTitle sx={{ px: 5, pt: 5, pb: 2, textAlign: 'center' }}>
        <Typography sx={dialogTitleSx}>Create New Payment Request</Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 5, pb: 5, pt: 2 }}>
        <Box sx={formGridSx}>
          {/* DAO Info Card */}
          <Box
            sx={{
              gridColumn: '1 / -1',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#1f2937',
              borderRadius: '12px',
              px: 3,
              py: 2.5,
            }}
          >
            <Typography
              sx={{
                color: colors.textPrimary,
                fontSize: '1.25rem',
                fontFamily: 'Questrial, sans-serif',
              }}
            >
              DAO information
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                sx={{
                  color: colors.textPrimary,
                  fontSize: '1.25rem',
                  fontWeight: 500,
                  fontFamily: 'Questrial, sans-serif',
                }}
              >
                {currentDao?.name || ''}
              </Typography>
              <Typography
                sx={{
                  color: colors.textSecondary,
                  fontSize: '0.95rem',
                  fontFamily: 'Questrial, sans-serif',
                }}
              >
                Code: {currentDao?.code || ''}
              </Typography>
            </Box>
          </Box>

          {/* Revenue Type */}
          <Box sx={fieldBlockStyles}>
            <Typography sx={fieldLabelStyles}>
              Revenue Type{' '}
              <Box component="span" sx={requiredSpanSx}>
                *
              </Box>
            </Typography>
            <Select
              value={serviceType}
              onChange={(e) => {
                setServiceType(e.target.value);
                setFormErrors((prev) => ({ ...prev, serviceType: undefined }));
              }}
              displayEmpty
              fullWidth
              renderValue={(val) => {
                if (!val) return <span style={{ color: 'inherit', opacity: 0.5 }}>Select Revenue Type</span>;
                return SERVICE_TYPE_OPTIONS.find((o) => o.value === val)?.label || val;
              }}
              sx={makeSelectSx(!!formErrors.serviceType, !!serviceType)}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="" disabled sx={{ color: colors.textSecondary, fontFamily: 'Questrial, sans-serif' }}>
                Select Revenue Type
              </MenuItem>
              {SERVICE_TYPE_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                  {serviceType === value && (
                    <Box component="span" sx={{ ml: 'auto', pl: 1, display: 'flex', alignItems: 'center' }}>
                      <TickCheckmark />
                    </Box>
                  )}
                </MenuItem>
              ))}
            </Select>
            {formErrors.serviceType && <Typography sx={fieldErrorTextSx}>{formErrors.serviceType}</Typography>}
          </Box>

          {/* Amount */}
          <Box sx={fieldBlockStyles}>
            <Typography sx={fieldLabelStyles}>
              Amount (USDT){' '}
              <Box component="span" sx={requiredSpanSx}>
                *
              </Box>
            </Typography>
            <TextField
              placeholder="0"
              value={amount}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || /^\d*\.?\d*$/.test(val)) {
                  setAmount(val);
                  if (val) setFormErrors((prev) => ({ ...prev, amount: undefined }));
                }
              }}
              fullWidth
              error={!!formErrors.amount}
              helperText={formErrors.amount}
              sx={formFieldStyles}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography>USDT</Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Invoice Number */}
          <Box sx={fieldBlockStyles}>
            <Typography sx={fieldLabelStyles}>Invoice Number</Typography>
            <TextField
              placeholder="INV-XXXX-XXXX"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              fullWidth
              sx={formFieldStyles}
            />
          </Box>

          {/* Expiration */}
          <Box sx={fieldBlockStyles}>
            <Typography sx={fieldLabelStyles}>
              Expiration{' '}
              <Box component="span" sx={requiredSpanSx}>
                *
              </Box>
            </Typography>
            <TextField
              placeholder="0"
              value={expiration}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || /^\d+$/.test(val)) {
                  setExpiration(val);
                  if (val) setFormErrors((prev) => ({ ...prev, expiration: undefined }));
                }
              }}
              fullWidth
              error={!!formErrors.expiration}
              helperText={formErrors.expiration}
              sx={formFieldStyles}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography>day(s)</Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Description */}
          <Box sx={{ ...fieldBlockStyles, gridColumn: { xs: 'auto', md: '1 / -1' } }}>
            <Typography sx={fieldLabelStyles}>Description</Typography>
            <TextField
              placeholder="Type your description here"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              sx={formFieldStyles}
            />
          </Box>
        </Box>

        {submitError && (
          <Alert severity="error" sx={{ ...alertErrorSx, mt: 2, mb: 0 }}>
            {submitError}
          </Alert>
        )}

        <Box sx={dialogBtnGridSx}>
          <Button onClick={handleClose} disabled={isSubmitting} sx={cancelBtnSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={isSubmitting}
            sx={{ ...buttonStyles, ...confirmBtnSx }}
          >
            {isSubmitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Confirm'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePaymentDialog;
